/**
 * MainAnimation
 * 메인 페이지 전용 애니메이션 클래스
 */
export class MainAnimation {
    constructor() {
        this.advantageSection = null;
        this.advantageItems = [];
        this.advantageIndicators = [];
        this.observers = [];
        this.interviewSwiper = null;
        this.heroTimeline = null;
        this.isTransitioning = false; // 솔루션 전환 중 여부
        this._scrollBlockers = null; // 스크롤 차단 리스너 참조
        this.logoMarqueeTimeline = null; // 로고 마퀴 타임라인
    }

    /**
     * 스크롤 임시 차단
     */
    disableScroll() {
        if (this._scrollBlockers) return;

        const prevent = (e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
        };
        const preventKeys = (e) => {
            const keys = [
                "ArrowUp",
                "ArrowDown",
                "PageUp",
                "PageDown",
                "Home",
                "End",
                " ",
                "Spacebar",
            ];
            if (keys.includes(e.key) || keys.includes(e.code)) {
                e.preventDefault();
                e.stopPropagation();
                return false;
            }
        };

        window.addEventListener("wheel", prevent, { passive: false });
        window.addEventListener("touchmove", prevent, { passive: false });
        window.addEventListener("keydown", preventKeys, { passive: false });
        this._scrollBlockers = { prevent, preventKeys };
    }

    /**
     * 스크롤 차단 해제
     */
    enableScroll() {
        if (!this._scrollBlockers) return;
        const { prevent, preventKeys } = this._scrollBlockers;
        window.removeEventListener("wheel", prevent, { passive: false });
        window.removeEventListener("touchmove", prevent, { passive: false });
        window.removeEventListener("keydown", preventKeys, { passive: false });
        this._scrollBlockers = null;
    }

    /**
     * Solutions 섹션 스냅 스크롤 + 클립패스 전환
     */
    solution() {
        const solutionsSection = document.getElementById("solutions-section");
        if (!solutionsSection) return;

        const items = solutionsSection.querySelectorAll(".no-solutions__item");
        const dots = solutionsSection.querySelectorAll(
            ".no-solutions__pagination-dot"
        );
        const swiperEl = document.getElementById("solutions-swiper");

        // 모바일용 Swiper 초기화 (1024px 이하)
        if (swiperEl && typeof Swiper !== "undefined") {
            const mediaQuery = window.matchMedia("(max-width: 1024px)");

            const initMobileSwiper = () => {
                if (mediaQuery.matches && !this.solutionsSwiper) {
                    this.solutionsSwiper = new Swiper("#solutions-swiper", {
                        slidesPerView: 1,
                        spaceBetween: 0,
                        speed: 800,
                        loop: false,
                        autoplay: {
                            delay: 5000,
                            disableOnInteraction: false,
                        },
                        navigation: {
                            nextEl: ".no-solutions__swiper-button-next",
                            prevEl: ".no-solutions__swiper-button-prev",
                        },
                        pagination: {
                            el: ".no-solutions__swiper-pagination",
                            type: "bullets",
                            clickable: true,
                        },
                        effect: "fade",
                        fadeEffect: {
                            crossFade: true,
                        },
                    });
                } else if (!mediaQuery.matches && this.solutionsSwiper) {
                    this.solutionsSwiper.destroy(true, true);
                    this.solutionsSwiper = null;
                }
            };

            // 초기화
            initMobileSwiper();

            // 리사이즈 시 재초기화
            mediaQuery.addEventListener("change", initMobileSwiper);
        }

        if (items.length === 0) return;

        // GSAP와 ScrollTrigger가 로드되었는지 확인
        if (
            typeof gsap === "undefined" ||
            typeof ScrollTrigger === "undefined"
        ) {
            console.warn("MainAnimation: GSAP or ScrollTrigger not found");
            return;
        }

        // ScrollToPlugin 등록 확인
        if (typeof ScrollToPlugin !== "undefined") {
            gsap.registerPlugin(ScrollToPlugin);
        }

        // 초기 상태: 첫 번째 아이템만 활성화
        items.forEach((item, index) => {
            if (index === 0) {
                item.classList.add("is-active");
                gsap.set(item, {
                    clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
                    opacity: 1,
                    visibility: "visible",
                });
            } else {
                gsap.set(item, {
                    clipPath: "polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)",
                    opacity: 0,
                    visibility: "hidden",
                });
            }
        });

        // 페이지네이션 dot 클릭 이벤트 (모든 화면 크기에서 작동)
        dots.forEach((dot, index) => {
            dot.addEventListener("click", () => {
                if (this.isTransitioning) return;

                // PC에서 ScrollTrigger 사용
                if (window.innerWidth >= 1025 && this.solutionScrollTrigger) {
                    const scrollProgress = index / (items.length - 1);
                    this.solutionScrollTrigger.refresh();
                    const start = this.solutionScrollTrigger.start;
                    const end = this.solutionScrollTrigger.end;
                    const targetScroll = start + (end - start) * scrollProgress;

                    // GSAP ScrollToPlugin 사용
                    if (
                        typeof ScrollToPlugin !== "undefined" &&
                        typeof gsap !== "undefined"
                    ) {
                        gsap.to(window, {
                            scrollTo: {
                                y: targetScroll,
                                autoKill: false,
                            },
                            duration: 0.8,
                            ease: "power2.out",
                        });
                    } else {
                        window.scrollTo({
                            top: targetScroll,
                            behavior: "smooth",
                        });
                    }
                } else {
                    // 모바일에서는 직접 전환
                    const targetItem = items[index];
                    if (targetItem) {
                        const currentActive = Array.from(items).findIndex(
                            (item) => item.classList.contains("is-active")
                        );
                        if (currentActive !== index) {
                            this.transitionToSolution(
                                items,
                                dots,
                                currentActive,
                                index
                            );
                        }
                    }
                }
            });
        });

        // matchMedia를 사용하여 PC에서만 ScrollTrigger 적용
        ScrollTrigger.matchMedia({
            // desktop (1025px 이상)
            "(min-width: 1025px)": () => {
                // 현재 활성 인덱스
                let currentIndex = 0;
                let isSnapping = false;
                let snapTimeout = null;
                let lastProgress = 0;
                let snapInProgress = false;
                let lastScrollTime = Date.now();

                // Lenis 인스턴스 확인 (전역 또는 app에서 가져오기)
                let lenis = null;
                if (window.lenis) {
                    lenis = window.lenis;
                } else if (
                    window.app &&
                    window.app.components &&
                    window.app.components.lenis
                ) {
                    lenis = window.app.components.lenis;
                }

                // 기존 trigger가 있으면 제거
                if (this.solutionScrollTrigger) {
                    this.solutionScrollTrigger.kill();
                    this.solutionScrollTrigger = null;
                }

                // ScrollTrigger 초기화를 약간 지연시켜 DOM이 완전히 준비되도록
                const initSolutionScrollTrigger = () => {
                    // ScrollTrigger로 섹션 고정 (pin)
                    this.solutionScrollTrigger = ScrollTrigger.create({
                        trigger: solutionsSection,
                        start: "top top",
                        end: `+=${items.length * 100}%`,
                        pin: true,
                        pinSpacing: true,
                        scrub: 1,
                        anticipatePin: 1,
                        invalidateOnRefresh: true,
                        snap: {
                            snapTo: 1 / (items.length - 1),
                            duration: 0.5,
                            ease: "power2.inOut",
                        },
                        onUpdate: (self) => {
                            const currentTime = Date.now();
                            const timeDelta = currentTime - lastScrollTime;
                            lastScrollTime = currentTime;

                            // progress 변화율 감지로 snap 중인지 판단
                            const progressDelta = Math.abs(
                                self.progress - lastProgress
                            );

                            // 큰 progress 변화이고 짧은 시간 내 발생하면 snap으로 판단
                            const isSnapDetected =
                                progressDelta > 0.15 && timeDelta < 100;

                            if (
                                isSnapDetected &&
                                !snapInProgress &&
                                !this.isTransitioning
                            ) {
                                // snap 시작 감지
                                snapInProgress = true;
                                isSnapping = true;

                                // Lenis 일시 정지
                                if (lenis && typeof lenis.stop === "function") {
                                    lenis.stop();
                                }

                                // 스크롤 차단
                                this.disableScroll();

                                // snap 완료 후 복구
                                if (snapTimeout) clearTimeout(snapTimeout);
                                snapTimeout = setTimeout(() => {
                                    snapInProgress = false;
                                    isSnapping = false;

                                    // Lenis 재개
                                    if (
                                        lenis &&
                                        typeof lenis.start === "function"
                                    ) {
                                        lenis.start();
                                    }

                                    // 스크롤 차단 해제
                                    this.enableScroll();
                                }, 600); // duration 0.5s + 여유 100ms
                            }

                            lastProgress = self.progress;

                            if (this.isTransitioning) return;
                            const newIndex = Math.round(
                                self.progress * (items.length - 1)
                            );

                            if (newIndex !== currentIndex) {
                                this.transitionToSolution(
                                    items,
                                    dots,
                                    currentIndex,
                                    newIndex
                                );
                                currentIndex = newIndex;
                            }
                        },
                        onEnter: () => {
                            // 섹션 진입 시 준비
                            lastProgress = 0;
                        },
                        onLeave: () => {
                            // 섹션을 벗어나면 모든 제어 해제
                            if (snapTimeout) clearTimeout(snapTimeout);
                            snapInProgress = false;
                            isSnapping = false;
                            if (lenis) lenis.start();
                            this.enableScroll();
                        },
                        onLeaveBack: () => {
                            // 섹션을 위로 벗어나면 모든 제어 해제
                            if (snapTimeout) clearTimeout(snapTimeout);
                            snapInProgress = false;
                            isSnapping = false;
                            if (lenis) lenis.start();
                            this.enableScroll();
                        },
                    });

                    // ScrollTrigger refresh
                    ScrollTrigger.refresh();
                };

                // DOM이 완전히 준비된 후 초기화
                setTimeout(() => {
                    initSolutionScrollTrigger();
                }, 100);
            },
            // mobile (1024px 이하) - ScrollTrigger 해제
            "(max-width: 1024px)": () => {
                // 모바일에서는 ScrollTrigger 해제
                if (this.solutionScrollTrigger) {
                    this.solutionScrollTrigger.kill();
                    this.solutionScrollTrigger = null;
                }
            },
        });
    }

    /**
     * 솔루션 전환 애니메이션 (대각선 클립패스)
     */
    transitionToSolution(items, dots, fromIndex, toIndex) {
        this.isTransitioning = true;
        this.disableScroll();
        const currentItem = items[fromIndex];
        const nextItem = items[toIndex];
        const tl = gsap.timeline();

        // 페이지네이션 업데이트
        dots.forEach((dot, index) => {
            dot.classList.toggle("is-active", index === toIndex);
        });

        // 현재 아이템 클래스 제거
        items.forEach((item) => item.classList.remove("is-active"));
        nextItem.classList.add("is-active");

        // 다음 아이템을 우측 상단에서 좌측 하단으로 대각선 클립패스
        tl.set(nextItem, {
            visibility: "visible",
            opacity: 1,
            clipPath: "polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)",
            zIndex: 2,
        })
            .to(
                nextItem,
                {
                    clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
                    duration: 1.2,
                    ease: "power3.inOut",
                },
                0
            )
            .to(
                currentItem,
                {
                    clipPath: "polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)",
                    duration: 1.2,
                    ease: "power3.inOut",
                    onComplete: () => {
                        gsap.set(currentItem, {
                            visibility: "hidden",
                            opacity: 0,
                            zIndex: 1,
                        });
                        this.isTransitioning = false;
                        this.enableScroll();
                    },
                },
                0
            );
    }

    /**
     * Hero 섹션 클립패스 애니메이션
     */
    hero() {
        const heroSection = document.getElementById("hero-section");
        if (!heroSection) return;

        // iframe으로 변경되었으므로 .no-hero__video 컨테이너를 사용
        const videoElement = heroSection.querySelector(".no-hero__video");
        const iframeElement = heroSection.querySelector("#main-film");
        const frameElement = heroSection.querySelector(".no-hero__frame");
        const titleLines = heroSection.querySelectorAll(".no-hero__title-line");
        const subElement = heroSection.querySelector(".no-hero__sub");
        const titleElement = heroSection.querySelector(".no-hero__title");

        if (!videoElement || !frameElement) return;

        // GSAP와 ScrollTrigger가 로드되었는지 확인
        if (
            typeof gsap === "undefined" ||
            typeof ScrollTrigger === "undefined"
        ) {
            console.warn("MainAnimation: GSAP or ScrollTrigger not found");
            return;
        }

        // 초기 텍스트 애니메이션 (SplitType + GSAP)
        this.initHeroTextAnimation(titleElement, titleLines);

        // frame의 위치와 크기 계산 (리사이즈 대응을 위해 함수로 감싸기)
        const calculateInitialClipPath = () => {
            const frameRect = frameElement.getBoundingClientRect();
            const sectionRect = heroSection.getBoundingClientRect();

            // frame의 상대적 위치 계산 (퍼센트)
            const frameTop =
                ((frameRect.top - sectionRect.top) / sectionRect.height) * 100;
            const frameLeft =
                ((frameRect.left - sectionRect.left) / sectionRect.width) * 100;
            const frameWidth = (frameRect.width / sectionRect.width) * 100;
            const frameHeight = (frameRect.height / sectionRect.height) * 100;

            // 평행사변형 clipPath 계산 (frame과 동일한 형태)
            // 평행사변형: polygon(40% 0%, 60% 0%, 45% 100%, 25% 100%)
            // 위쪽 40%~60% (20% 너비), 아래쪽 25%~45% (20% 너비) - 같은 너비로 평행사변형
            // 평행사변형 특성: 위쪽과 아래쪽의 너비가 같고, 왼쪽과 오른쪽이 기울어짐 (기울기 15% - 가파른 대각선)
            // frame 내부에서의 비율을 섹션 기준으로 변환
            const topLeftOffset =
                ((frameRect.width * 0.4) / sectionRect.width) * 100;
            const topRightOffset =
                ((frameRect.width * 0.6) / sectionRect.width) * 100;
            const bottomLeftOffset =
                ((frameRect.width * 0.25) / sectionRect.width) * 100;
            const bottomRightOffset =
                ((frameRect.width * 0.45) / sectionRect.width) * 100;

            const initialClipPath = `polygon(
                ${frameLeft + topLeftOffset}% ${frameTop}%,
                ${frameLeft + topRightOffset}% ${frameTop}%,
                ${frameLeft + bottomRightOffset}% ${frameTop + frameHeight}%,
                ${frameLeft + bottomLeftOffset}% ${frameTop + frameHeight}%
            )`;

            return initialClipPath;
        };

        const initialClipPath = calculateInitialClipPath();

        // iframe이 로드될 때까지 기다린 후 초기 상태 설정
        const initAnimation = () => {
            // 초기 상태 설정
            gsap.set(videoElement, {
                clipPath: initialClipPath.replace(/\s+/g, " ").trim(), // 공백 정리
            });

            // 애니메이션 시작
            this.startHeroAnimation();
        };

        // iframe이 있는 경우 로드 대기, 없으면 바로 시작
        if (iframeElement) {
            // iframe 로드 이벤트 대기 (YouTube iframe은 로드 시간이 걸릴 수 있음)
            iframeElement.addEventListener(
                "load",
                () => {
                    // 약간의 지연 후 애니메이션 시작 (iframe 내부 컨텐츠가 완전히 로드되도록)
                    setTimeout(() => {
                        initAnimation();
                    }, 100);
                },
                { once: true }
            );

            // 타임아웃 설정 (3초 후 강제 시작 - 네트워크 문제 대비)
            setTimeout(() => {
                if (!videoElement.style.clipPath) {
                    initAnimation();
                }
            }, 3000);
        } else {
            // iframe이 없으면 바로 시작 (기존 video 요소 대응)
            initAnimation();
        }
    }

    /**
     * Hero 애니메이션 시작 (iframe 로드 후 호출)
     */
    startHeroAnimation() {
        const heroSection = document.getElementById("hero-section");
        if (!heroSection) return;

        const videoElement = heroSection.querySelector(".no-hero__video");
        const frameElement = heroSection.querySelector(".no-hero__frame");
        const titleLines = heroSection.querySelectorAll(".no-hero__title-line");
        const subElement = heroSection.querySelector(".no-hero__sub");

        if (!videoElement || !frameElement) return;

        // GSAP와 ScrollTrigger가 로드되었는지 확인
        if (
            typeof gsap === "undefined" ||
            typeof ScrollTrigger === "undefined"
        ) {
            console.warn("MainAnimation: GSAP or ScrollTrigger not found");
            return;
        }

        // frame의 위치와 크기 계산 (리사이즈 대응을 위해 함수로 감싸기)
        const calculateInitialClipPath = () => {
            const frameRect = frameElement.getBoundingClientRect();
            const sectionRect = heroSection.getBoundingClientRect();

            // frame의 상대적 위치 계산 (퍼센트)
            const frameTop =
                ((frameRect.top - sectionRect.top) / sectionRect.height) * 100;
            const frameLeft =
                ((frameRect.left - sectionRect.left) / sectionRect.width) * 100;
            const frameWidth = (frameRect.width / sectionRect.width) * 100;
            const frameHeight = (frameRect.height / sectionRect.height) * 100;

            // 평행사변형 clipPath 계산 (frame과 동일한 형태)
            const topLeftOffset =
                ((frameRect.width * 0.4) / sectionRect.width) * 100;
            const topRightOffset =
                ((frameRect.width * 0.6) / sectionRect.width) * 100;
            const bottomLeftOffset =
                ((frameRect.width * 0.25) / sectionRect.width) * 100;
            const bottomRightOffset =
                ((frameRect.width * 0.45) / sectionRect.width) * 100;

            const initialClipPath = `polygon(
                ${frameLeft + topLeftOffset}% ${frameTop}%,
                ${frameLeft + topRightOffset}% ${frameTop}%,
                ${frameLeft + bottomRightOffset}% ${frameTop + frameHeight}%,
                ${frameLeft + bottomLeftOffset}% ${frameTop + frameHeight}%
            )`;

            return initialClipPath;
        };

        // 리사이즈 대응 (선택사항)
        let resizeTimer;
        window.addEventListener("resize", () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                const newClipPath = calculateInitialClipPath();
                gsap.set(videoElement, {
                    clipPath: newClipPath.replace(/\s+/g, " ").trim(),
                });
            }, 250);
        });

        // sub 요소 초기 상태 (이미 CSS에서 숨김 처리되어 있지만 확실히)
        gsap.set(subElement, {
            opacity: 0,
            visibility: "hidden",
            y: 30,
        });

        // 애니메이션 타임라인 생성 (pin과 함께)
        this.heroTimeline = gsap.timeline({
            scrollTrigger: {
                trigger: heroSection,
                start: "top top",
                end: "+=200%", // 스크롤 거리 조정
                scrub: 1.5,
                pin: true,
                pinSpacing: true,
                anticipatePin: 1,
                markers: false,
            },
        });

        // 2단계: title-line이 멋지게 사라지기 - overflow hidden으로 클립되는 효과
        this.heroTimeline.to(
            titleLines,
            {
                y: "-50%",
                duration: 0.8,
                ease: "power3.in",
                stagger: 0.2,
            },
            0.3
        );

        // clip-path로 위에서 아래로 클립되는 효과 추가
        this.heroTimeline.to(
            titleLines,
            {
                clipPath: "inset(100% 0% 0% 0%)",
                duration: 0.8,
                ease: "power3.in",
                stagger: 0.2,
            },
            0.3
        );

        // 1단계: 비디오 clipPath 확장 (평행사변형 -> 전체 화면) - 전체 시간의 0~60%
        this.heroTimeline.to(
            videoElement,
            {
                clipPath: "polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)",
                duration: 0.6,
                ease: "power2.inOut",
            },
            "-=0.25"
        );

        // 3단계: sub 요소 나타나기 - 전체 시간의 70~100%
        this.heroTimeline.to(subElement, {
            opacity: 1,
            visibility: "visible",
            y: 0,
            duration: 0.6,
            ease: "power2.out",
        });

        // 4단계: sub 요소가 나타난 후 0.5초 멈춤
        this.heroTimeline.to(subElement, {}, 0.5);
    }

    /**
     * Hero 텍스트 초기 애니메이션 (SplitType + GSAP)
     */
    initHeroTextAnimation(titleElement, titleLines) {
        if (!titleElement || !titleLines.length) return;

        // SplitType이 로드되었는지 확인
        if (typeof SplitType === "undefined") {
            console.warn(
                "MainAnimation: SplitType not found, using fallback animation"
            );
            this.fallbackTextAnimation(titleElement, titleLines);
            return;
        }

        // 각 title-line을 SplitType으로 분리
        const splitElements = [];
        titleLines.forEach((line, index) => {
            const split = new SplitType(line, {
                type: "chars,words",
                tagName: "span",
            });

            // 각 문자를 초기 상태로 설정
            gsap.set(split.chars, {
                opacity: 0,
                y: 50,
                rotationX: -90,
            });

            splitElements.push(split);
        });

        // frame도 초기 상태 설정
        const frameElement = document.querySelector(".no-hero__frame");
        if (frameElement) {
            gsap.set(frameElement, {
                opacity: 0,
                scale: 0.8,
                rotationX: -15,
            });
        }

        // title 전체 초기 상태
        gsap.set(titleElement, {
            opacity: 0,
        });

        // 애니메이션 타임라인 생성
        const textTimeline = gsap.timeline({
            delay: 0.3,
            onComplete: () => {
                // 애니메이션 완료 후 title 표시
                gsap.set(titleElement, {
                    opacity: 1,
                });
            },
        });

        // 첫 번째 라인 애니메이션
        if (splitElements[0] && splitElements[0].chars) {
            textTimeline.to(
                splitElements[0].chars,
                {
                    opacity: 1,
                    y: 0,
                    rotationX: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    stagger: {
                        amount: 0.6,
                        from: "start",
                    },
                },
                0
            );
        }

        // frame 애니메이션
        if (frameElement) {
            textTimeline.to(
                frameElement,
                {
                    opacity: 1,
                    scale: 1,
                    rotationX: 15,
                    duration: 1,
                    ease: "power3.out",
                },
                0.4
            );
        }

        // 두 번째 라인 애니메이션
        if (splitElements[1] && splitElements[1].chars) {
            textTimeline.to(
                splitElements[1].chars,
                {
                    opacity: 1,
                    y: 0,
                    rotationX: 0,
                    duration: 0.8,
                    ease: "power3.out",
                    stagger: {
                        amount: 0.6,
                        from: "start",
                    },
                },
                0.8
            );
        }

        // title 전체 페이드 인
        textTimeline.to(
            titleElement,
            {
                opacity: 1,
                duration: 0.5,
                ease: "power2.out",
            },
            0.2
        );
    }

    /**
     * SplitType이 없을 때 사용하는 폴백 애니메이션
     */
    fallbackTextAnimation(titleElement, titleLines) {
        // title 전체 초기 상태
        gsap.set(titleElement, {
            opacity: 0,
            y: 30,
        });

        // frame 초기 상태
        const frameElement = document.querySelector(".no-hero__frame");
        if (frameElement) {
            gsap.set(frameElement, {
                opacity: 0,
                scale: 0.8,
            });
        }

        // 애니메이션 타임라인
        const textTimeline = gsap.timeline({
            delay: 0.3,
        });

        // 첫 번째 라인
        if (titleLines[0]) {
            textTimeline.from(
                titleLines[0],
                {
                    opacity: 0,
                    y: 50,
                    duration: 0.8,
                    ease: "power3.out",
                },
                0
            );
        }

        // frame
        if (frameElement) {
            textTimeline.from(
                frameElement,
                {
                    opacity: 0,
                    scale: 0.8,
                    duration: 1,
                    ease: "power3.out",
                },
                0.4
            );
        }

        // 두 번째 라인
        if (titleLines[1]) {
            textTimeline.from(
                titleLines[1],
                {
                    opacity: 0,
                    y: 50,
                    duration: 0.8,
                    ease: "power3.out",
                },
                0.8
            );
        }

        // title 전체
        textTimeline.to(
            titleElement,
            {
                opacity: 1,
                y: 0,
                duration: 0.5,
                ease: "power2.out",
            },
            0.2
        );
    }

    /**
     * Advantages 섹션 스크롤 트리거 초기화
     */
    advantage() {
        const section = document.getElementById("advantages-section");
        if (!section) return;

        // GSAP와 ScrollTrigger가 로드되었는지 확인
        if (
            typeof gsap === "undefined" ||
            typeof ScrollTrigger === "undefined"
        ) {
            console.warn("MainAnimation: GSAP or ScrollTrigger not found");
            return;
        }

        this.advantageSection = section;
        const sidebar = section.querySelector(".no-advantages__sidebar");
        const content = section.querySelector(".no-advantages__content");
        const items = section.querySelectorAll(".no-advantages__item");
        const indicators = section.querySelectorAll(
            ".no-advantages__indicator"
        );

        if (items.length === 0 || indicators.length === 0 || !sidebar) return;

        this.advantageItems = Array.from(items);
        this.advantageIndicators = Array.from(indicators);

        // 초기 첫 번째 항목 활성화
        this.setActiveItem(0);

        // Intersection Observer 설정 (모든 화면 크기에서 작동)
        const observerOptions = {
            root: null,
            rootMargin: "-30% 0px -50% 0px", // 뷰포트 상단 30%, 하단 50% 지점에서 트리거
            threshold: [0, 0.25, 0.5, 0.75, 1],
        };

        const observerCallback = (entries) => {
            entries.forEach((entry) => {
                const itemIndex = parseInt(
                    entry.target.getAttribute("data-item"),
                    10
                );

                if (entry.isIntersecting && entry.intersectionRatio > 0.25) {
                    this.setActiveItem(itemIndex);
                }
            });
        };

        // 각 항목에 대해 Observer 생성 (모든 화면 크기)
        this.advantageItems.forEach((item) => {
            const observer = new IntersectionObserver(
                observerCallback,
                observerOptions
            );
            observer.observe(item);
            this.observers.push(observer);
        });

        // 인디케이터 클릭 이벤트 (모든 화면 크기)
        this.advantageIndicators.forEach((indicator, index) => {
            indicator.addEventListener("click", () => {
                this.scrollToItem(index);
            });
        });

        // matchMedia를 사용하여 PC에서만 ScrollTrigger 적용
        ScrollTrigger.matchMedia({
            // desktop (1025px 이상)
            "(min-width: 1025px)": () => {
                // ScrollTrigger로 사이드바 pin 설정
                // 콘텐츠 영역의 높이를 기준으로 스크롤 거리 계산
                const calculateScrollDistance = () => {
                    const viewportHeight = window.innerHeight;
                    const headerOffset = 120; // 헤더 높이 고려

                    // 콘텐츠의 실제 높이 (gap 포함)
                    const contentHeight = content.scrollHeight;
                    const sectionHeight = section.offsetHeight;

                    // 섹션 초기 높이 (뷰포트 기준, 헤더 제외)
                    const initialVisibleHeight = viewportHeight - headerOffset;

                    // 마지막 항목이 뷰포트 하단에 완전히 보이려면
                    // 콘텐츠가 스크롤되면서 마지막 항목의 하단이 뷰포트 하단에 오도록 해야 함
                    // 스크롤 거리 = 콘텐츠 전체 높이 - 초기 보이는 높이 + 여유 공간

                    if (contentHeight > initialVisibleHeight) {
                        // 마지막 항목이 완전히 보이도록 충분한 스크롤 거리
                        // contentHeight에서 초기 보이는 높이를 빼고, 뷰포트 높이의 40% 여유를 추가
                        // 이렇게 하면 마지막 항목이 위쪽에 잘리지 않고 완전히 보임
                        const scrollNeeded =
                            contentHeight - initialVisibleHeight;
                        return scrollNeeded + viewportHeight * 0.4;
                    }

                    // 콘텐츠가 짧은 경우: 최소 스크롤 거리
                    return Math.max(viewportHeight * 0.5, contentHeight * 0.5);
                };

                // 스크롤 거리 계산 및 pin 설정
                const initPin = () => {
                    const scrollDistance = calculateScrollDistance();

                    // scrollDistance가 너무 작으면 pin이 작동하지 않으므로 최소값 보장
                    // 하지만 너무 크면 빈 공간이 생기므로 정확히 계산한 값 사용
                    const finalScrollDistance = Math.max(
                        scrollDistance,
                        window.innerHeight * 0.5
                    );

                    // 기존 trigger가 있으면 제거
                    if (this.advantageScrollTrigger) {
                        this.advantageScrollTrigger.kill();
                        this.advantageScrollTrigger = null;
                    }

                    this.advantageScrollTrigger = ScrollTrigger.create({
                        trigger: section,
                        start: "top top",
                        end: `+=${finalScrollDistance}`,
                        pin: sidebar,
                        pinSpacing: true,
                        anticipatePin: 1,
                        markers: false,
                        invalidateOnRefresh: true,
                    });
                };

                // 이미지 로드 완료 후 정확한 높이 측정
                const waitForImages = () => {
                    const images = content.querySelectorAll("img");
                    let loadedCount = 0;
                    const totalImages = images.length;

                    if (totalImages === 0) {
                        // 이미지가 없으면 바로 초기화
                        ScrollTrigger.refresh();
                        initPin();
                        return;
                    }

                    const checkComplete = () => {
                        loadedCount++;
                        if (loadedCount === totalImages) {
                            // 모든 이미지 로드 완료 후 초기화
                            setTimeout(() => {
                                ScrollTrigger.refresh();
                                initPin();
                            }, 100);
                        }
                    };

                    images.forEach((img) => {
                        if (img.complete) {
                            checkComplete();
                        } else {
                            img.addEventListener("load", checkComplete);
                            img.addEventListener("error", checkComplete); // 에러 발생 시에도 진행
                        }
                    });
                };

                // DOM이 완전히 렌더링된 후 이미지 로드 대기
                setTimeout(() => {
                    waitForImages();
                }, 100);

                // 리사이즈 대응
                let resizeTimer;
                const resizeHandler = () => {
                    clearTimeout(resizeTimer);
                    resizeTimer = setTimeout(() => {
                        // 리사이즈 시 모든 ScrollTrigger 갱신
                        ScrollTrigger.refresh();
                    }, 250);
                };
                window.addEventListener("resize", resizeHandler);
            },
            // mobile (1024px 이하) - ScrollTrigger 해제
            "(max-width: 1024px)": () => {
                // 모바일에서는 ScrollTrigger 해제
                if (this.advantageScrollTrigger) {
                    this.advantageScrollTrigger.kill();
                    this.advantageScrollTrigger = null;
                }
            },
        });
    }

    /**
     * 활성 항목 설정
     */
    setActiveItem(index) {
        if (index < 0 || index >= this.advantageItems.length) return;

        // 모든 항목과 인디케이터 비활성화
        this.advantageItems.forEach((item) => {
            item.classList.remove("is-active");
        });
        this.advantageIndicators.forEach((indicator) => {
            indicator.classList.remove("is-active");
        });

        // 선택된 항목과 인디케이터 활성화
        if (this.advantageItems[index]) {
            this.advantageItems[index].classList.add("is-active");
        }
        if (this.advantageIndicators[index]) {
            this.advantageIndicators[index].classList.add("is-active");
        }
    }

    /**
     * 특정 항목으로 스크롤
     */
    scrollToItem(index) {
        if (index < 0 || index >= this.advantageItems.length) return;

        const item = this.advantageItems[index];
        if (!item) return;

        const itemTop = item.getBoundingClientRect().top + window.pageYOffset;
        const offset = 200; // 헤더 높이 등 고려한 오프셋

        window.scrollTo({
            top: itemTop - offset,
            behavior: "smooth",
        });
    }

    /**
     * Global Network 섹션 애니메이션
     */
    network() {
        const networkSection = document.getElementById("network-section");
        if (!networkSection) return;

        // GSAP와 ScrollTrigger가 로드되었는지 확인
        if (
            typeof gsap === "undefined" ||
            typeof ScrollTrigger === "undefined"
        ) {
            console.warn("MainAnimation: GSAP or ScrollTrigger not found");
            return;
        }

        const mapImg = networkSection.querySelector(".no-network__map img");
        const overlay = networkSection.querySelector(".no-network__overlay");
        const header = networkSection.querySelector(".no-network__header");
        const stats = networkSection.querySelector(".no-network__stats");
        const statNumbers = networkSection.querySelectorAll(
            ".no-network__stat-number em"
        );

        if (!statNumbers || statNumbers.length === 0) return;

        // 공통 카운터업 함수 정의
        const startCounterUp = () => {
            statNumbers.forEach((statNumber, index) => {
                // data-value 속성에서 숫자 값 가져오기
                const parent = statNumber.parentElement;
                const dataValue = parent.getAttribute("data-value");
                const number = dataValue ? parseInt(dataValue) : 0;

                if (!isNaN(number) && number > 0) {
                    // 초기값을 0으로 설정
                    statNumber.textContent = "0";

                    // 순차적으로 카운터업 시작
                    setTimeout(() => {
                        const startTime = Date.now();
                        const duration = 1500;

                        const updateCounter = () => {
                            const elapsed = Date.now() - startTime;
                            const progress = Math.min(elapsed / duration, 1);

                            // easeOut 효과
                            const eased = 1 - Math.pow(1 - progress, 3);
                            const current = Math.floor(number * eased);

                            statNumber.textContent = current.toLocaleString();

                            if (progress < 1) {
                                requestAnimationFrame(updateCounter);
                            } else {
                                statNumber.textContent =
                                    number.toLocaleString();
                            }
                        };

                        updateCounter();
                    }, index * 150); // 순차적으로 카운터업 (150ms 간격)
                }
            });
        };

        // ScrollTrigger.matchMedia를 사용하여 반응형 처리
        ScrollTrigger.matchMedia({
            // desktop (1025px 이상)
            "(min-width: 1025px)": () => {
                if (!mapImg || !overlay || !header || !stats) return;

                // 초기 상태 설정
                gsap.set(mapImg, {
                    scale: 0.8,
                    opacity: 0.5,
                    filter: "blur(10px)",
                });

                gsap.set(overlay, {
                    opacity: 0,
                });

                gsap.set([header, stats], {
                    opacity: 0,
                    y: 30,
                });

                // ScrollTrigger 초기화를 약간 지연시켜 DOM이 완전히 준비되도록
                const initNetworkScrollTrigger = () => {
                    // 타임라인 생성
                    const tl = gsap.timeline({
                        scrollTrigger: {
                            trigger: networkSection,
                            start: "top top",
                            end: "+=200%",
                            scrub: 1,
                            pin: true,
                            pinSpacing: true,
                            anticipatePin: 1,
                            invalidateOnRefresh: true,
                        },
                    });

                    // 1. 맵 스케일 애니메이션 (0.8 → 1.0, 흐림 → 선명)
                    tl.to(
                        mapImg,
                        {
                            scale: 1,
                            opacity: 1,
                            filter: "blur(0px)",
                            duration: 1,
                            ease: "power2.out",
                        },
                        0
                    );

                    // 2. 오버레이 나타남 (맵이 완전히 선명해진 후 1초 대기)
                    tl.to(
                        overlay,
                        {
                            opacity: 1,
                            duration: 0.8,
                            ease: "power2.inOut",
                        },
                        2.0
                    );

                    // 3. 헤더 나타남
                    tl.to(
                        header,
                        {
                            opacity: 1,
                            y: 0,
                            duration: 0.8,
                            ease: "power2.out",
                        },
                        2.3
                    );

                    // 4. Stats 나타남 및 카운터업 (동시에 시작)
                    tl.to(
                        stats,
                        {
                            opacity: 1,
                            y: 0,
                            duration: 0.8,
                            ease: "power2.out",
                            onStart: () => {
                                // stats 애니메이션 시작과 동시에 카운터업 시작
                                startCounterUp();
                            },
                        },
                        2.5
                    );

                    // ScrollTrigger refresh
                    ScrollTrigger.refresh();
                };

                // DOM이 완전히 준비된 후 초기화
                setTimeout(() => {
                    initNetworkScrollTrigger();
                }, 100);
            },
            // mobile (1024px 이하)
            "(max-width: 1024px)": () => {
                // 모바일에서는 카운터업만 실행
                // ScrollTrigger로 섹션이 뷰포트에 들어올 때 카운터업 시작
                ScrollTrigger.create({
                    trigger: networkSection,
                    start: "top 80%",
                    once: true,
                    onEnter: () => {
                        startCounterUp();
                    },
                });
            },
        });
    }

    /**
     * Logo Marquee 초기화 (GSAP 사용)
     */
    logoMarquee() {
        const marqueeSection = document.querySelector(".no-logo-marquee");
        if (!marqueeSection) return;

        const content = marqueeSection.querySelector(
            ".no-logo-marquee__content"
        );
        if (!content) return;

        // GSAP가 로드되었는지 확인
        if (typeof gsap === "undefined") {
            console.warn("MainAnimation: GSAP not found");
            return;
        }

        // 이미지 확인
        const images = content.querySelectorAll("img");
        if (images.length === 0) return;

        // 기존 애니메이션 제거
        if (this.logoMarqueeTimeline) {
            this.logoMarqueeTimeline.kill();
        }

        // 이미지 로드 확인
        let loadedCount = 0;
        const totalImages = images.length;

        const initMarquee = () => {
            // content의 전체 너비 계산
            const contentWidth = content.scrollWidth;

            // 로고들을 복제하여 무한 스크롤 효과 생성
            const clone = content.cloneNode(true);
            clone.classList.add("no-logo-marquee__content--clone");
            content.parentElement.appendChild(clone);

            // 초기 위치 설정 - 겹치지 않게 원본 바로 뒤에 배치
            gsap.set(content, { x: 0 });
            gsap.set(clone, { x: contentWidth });

            // 속도 계산 (50px/s)
            const speed = 50;
            const duration = contentWidth / speed;

            // 일시정지 플래그
            let isPaused = false;
            let currentAnimation = null;

            // 애니메이션 생성 (끊김 없이 무한 반복)
            const animate = () => {
                if (isPaused) return; // 일시정지 중이면 실행하지 않음

                // 원본과 클론을 동시에 왼쪽으로 이동
                currentAnimation = gsap.to([content, clone], {
                    x: `-=${contentWidth}`,
                    duration: duration,
                    ease: "none",
                    onComplete: () => {
                        // 한 사이클 완료 시 위치 조정
                        const contentX = parseFloat(
                            gsap.getProperty(content, "x")
                        );
                        const cloneX = parseFloat(gsap.getProperty(clone, "x"));

                        // 첫 번째가 완전히 사라지면 뒤로 이동 (끊김 없이)
                        if (contentX <= -contentWidth) {
                            gsap.set(content, { x: cloneX + contentWidth });
                        }
                        if (cloneX <= -contentWidth) {
                            gsap.set(clone, { x: contentX + contentWidth });
                        }

                        // 계속 반복 (끊김 없이)
                        animate();
                    },
                });
            };

            // 호버 시 일시 정지
            marqueeSection.addEventListener("mouseenter", () => {
                if (!isPaused) {
                    isPaused = true;
                    if (currentAnimation) {
                        currentAnimation.pause();
                    }
                }
            });

            marqueeSection.addEventListener("mouseleave", () => {
                if (isPaused) {
                    isPaused = false;
                    if (currentAnimation) {
                        currentAnimation.resume();
                    } else {
                        // 애니메이션이 없으면 다시 시작
                        animate();
                    }
                }
            });

            // 애니메이션 시작
            this.logoMarqueeTimeline = {
                pause: () => {
                    isPaused = true;
                    if (currentAnimation) currentAnimation.pause();
                },
                resume: () => {
                    isPaused = false;
                    if (currentAnimation) {
                        currentAnimation.resume();
                    } else {
                        animate();
                    }
                },
                kill: () => {
                    isPaused = true;
                    if (currentAnimation) {
                        currentAnimation.kill();
                        currentAnimation = null;
                    }
                },
            };

            // 애니메이션 시작
            animate();
        };

        const checkImageLoad = () => {
            loadedCount++;
            if (loadedCount === totalImages) {
                // 모든 이미지 로드 완료 후 초기화
                setTimeout(() => {
                    initMarquee();
                }, 100);
            }
        };

        // 이미지 로드 상태 확인
        images.forEach((img) => {
            if (img.complete && img.naturalHeight !== 0) {
                checkImageLoad();
            } else {
                img.addEventListener("load", checkImageLoad, { once: true });
                img.addEventListener("error", () => {
                    console.warn(
                        "MainAnimation: Image failed to load",
                        img.src
                    );
                    checkImageLoad();
                });
            }
        });

        // 모든 이미지가 이미 로드된 경우
        if (loadedCount === totalImages) {
            setTimeout(() => {
                initMarquee();
            }, 100);
        }
    }

    /**
     * Interview 슬라이더 초기화
     */
    interview() {
        const sliderEl = document.getElementById("interview-slider");
        if (!sliderEl) return;

        // Swiper가 로드되었는지 확인
        if (typeof Swiper === "undefined") {
            console.warn("MainAnimation: Swiper library not found");
            return;
        }

        // 기존 스와이퍼가 있으면 제거
        if (this.interviewSwiper) {
            this.interviewSwiper.destroy(true, true);
            this.interviewSwiper = null;
        }

        // 모든 화면 크기에서 작동
        this.interviewSwiper = new Swiper("#interview-slider", {
            loop: true,
            autoplay: {
                delay: 5000,
                disableOnInteraction: false,
            },
            speed: 1000,
            effect: "fade",
            fadeEffect: {
                crossFade: true,
            },
            pagination: {
                el: ".no-interview__pagination",
                type: "bullets",
                clickable: true,
            },
            navigation: {
                nextEl: ".no-interview__nav-btn--next",
                prevEl: ".no-interview__nav-btn--prev",
            },
        });
    }

    /**
     * 정리
     */
    destroy() {
        this.observers.forEach((observer) => {
            observer.disconnect();
        });
        this.observers = [];

        if (this.interviewSwiper) {
            this.interviewSwiper.destroy(true, true);
            this.interviewSwiper = null;
        }

        if (this.solutionsSwiper) {
            this.solutionsSwiper.destroy(true, true);
            this.solutionsSwiper = null;
        }

        if (this.heroTimeline) {
            this.heroTimeline.kill();
            this.heroTimeline = null;
        }

        if (this.logoMarqueeTimeline) {
            this.logoMarqueeTimeline.kill();
            this.logoMarqueeTimeline = null;
        }

        // ScrollTrigger 개별 정리
        if (this.advantageScrollTrigger) {
            this.advantageScrollTrigger.kill();
            this.advantageScrollTrigger = null;
        }

        if (this.solutionScrollTrigger) {
            this.solutionScrollTrigger.kill();
            this.solutionScrollTrigger = null;
        }

        // ScrollTrigger 전체 정리 (남은 것들)
        if (typeof ScrollTrigger !== "undefined") {
            ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
        }
    }
}
